//@ts-ignore
import React from 'react';
import { Box } from '@mui/material';
import { LucideIcon as LucideIconType } from 'lucide-react';
import { SxProps, Theme } from '@mui/material';
import { fontAwesomeToLucideMap } from './lucide-mapping';

export type TFAIconProps = {
  icon: LucideIconType | string;
  size?: number | string;
  color?: string;
  sx?: SxProps<Theme>;
  fontSize?: number | string;
  fixedWidth?: boolean;
  flip?: 'horizontal' | 'vertical' | 'both';
  [key: string]: any; // For additional props compatibility
};

export function FAIcon({
  icon,
  size = 18,
  color,
  sx,
  fontSize,
  fixedWidth,
  flip,
  ...other
}: TFAIconProps) {
  // Handle different icon types with proper error handling
  let Icon: LucideIconType;

  try {
    if (typeof icon === 'function') {
      // Direct Lucide icon component
      Icon = icon;
    } else if (typeof icon === 'object' && icon && 'displayName' in icon) {
      // Re-exported Lucide icon (has displayName property)
      Icon = icon as LucideIconType;
    } else if (typeof icon === 'object' && icon && '$$typeof' in icon) {
      // React forward ref component (Lucide icons)
      const symbolString = String((icon as any).$$typeof);
      if (symbolString.includes('react.forward_ref')) {
        Icon = icon as LucideIconType;
      } else {
        console.warn('FAIcon: Unknown React component passed as icon:', icon);
        return null;
      }
    } else if (typeof icon === 'string') {
      // FontAwesome icon name string - use mapping
      Icon = fontAwesomeToLucideMap[icon];
      if (!Icon) {
        console.warn('FAIcon: No Lucide equivalent found for FontAwesome icon:', icon);
        return null;
      }
    } else {
      console.warn('FAIcon: Invalid icon provided:', {
        icon,
        type: typeof icon,
        isBoolean: typeof icon === 'boolean',
        isNull: icon === null,
        isUndefined: icon === undefined
      });
      return null;
    }
  } catch (error) {
    console.error('FAIcon: Error processing icon:', error, { icon });
    return null;
  }

  // Use fontSize if provided, otherwise use size
  const finalSize = fontSize || size;
  
  // Convert string sizes to numbers if needed
  const numericSize = typeof finalSize === 'string' 
    ? parseInt(finalSize.replace('px', ''), 10) 
    : finalSize;

  // Handle flip transformations
  let transform = '';
  if (flip === 'horizontal') {
    transform = 'scaleX(-1)';
  } else if (flip === 'vertical') {
    transform = 'scaleY(-1)';
  } else if (flip === 'both') {
    transform = 'scale(-1, -1)';
  }

  try {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: fixedWidth ? '1.25em' : 'auto',
          transform,
          padding: 0,
          margin: 0,
          ...sx,
        }}
        {...other}
      >
        <Icon 
          size={numericSize} 
          color={color || 'currentColor'}
        />
      </Box>
    );
  } catch (error) {
    console.error('FAIcon: Error rendering icon:', error, { icon, finalSize, color });
    // Return a fallback icon or empty span to prevent crashes
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: fixedWidth ? '1.25em' : 'auto',
          padding: 0,
          margin: 0,
          ...sx,
        }}
        {...other}
      >
        {/* Fallback: show a simple question mark or nothing */}
        <span style={{ fontSize: finalSize, color: color || 'currentColor' }}>?</span>
      </Box>
    );
  }
}
